// @flow

import React from "react";
import { useSelector } from "react-redux";
import { isEmpty } from "ramda";
import {
  SelecaoAjusteDias,
  SelecaoCasasDecimais,
  SelecaoIndices,
  SelecaoPeriodicidade,
  SelecaoPeriodo,
  SelecaoJanelaMovel,
} from "../configuracoes";
import {
  type ErrosSelecao,
  ID_CONFIGURACAO_RELATORIO_MEDIDA_ANALISE_ESTILO_R2,
} from "../../../model";
import {
  type GeradorMedidasSelecionadas,
  type State,
  gerarMedidasSelecionadasPorPeriodo,
  getDataBaseSelecionadaAsString,
  getEdicaoMedida,
  getIndicesEBenchmarksPersonalizados,
  getIndicesSelecionados,
  i18n,
  isModoSeries,
  analiseValorDaMedida,
} from "../../../state";

import { randomId } from "../../../util";

export const criaFuncaoValidacaoPeriodoComIndices = (
  state: State
) => (): ?ErrosSelecao => {
  const erros: ErrosSelecao = {};
  const { translate } = i18n(state);

  if (isEmpty(getIndicesSelecionados(state)))
    erros.indicesNaoSelecionados = translate(
      "configuracaoRelatorio.indices.nenhumSelecionado"
    );

  return isEmpty(erros) ? null : erros;
};

export const processarPeriodoComIndices: GeradorMedidasSelecionadas = (
  state,
  medidas
) => {
  const edicaoMedida = getEdicaoMedida(state);
  const modoSeries = isModoSeries(state);
  const dataBase = modoSeries ? null : getDataBaseSelecionadaAsString(state);
  const indicesSelecionados = new Set(edicaoMedida.indices || []);
  const indices = getIndicesEBenchmarksPersonalizados(state)
    .map(indice => indice.nome)
    .filter(indice => indicesSelecionados.has(indice));
  const ajusteDias = modoSeries ? null : edicaoMedida.ajusteDias;
  const periodicidade = modoSeries ? null : edicaoMedida.periodicidade;

  return gerarMedidasSelecionadasPorPeriodo(state, medidas).flatMap(
    medidaSelecionada => {
      const idGrupoMedidasSelecionadas = randomId();

      if (
        medidaSelecionada.medida.codigoOriginal ===
        ID_CONFIGURACAO_RELATORIO_MEDIDA_ANALISE_ESTILO_R2
      ) {
        return {
          ...medidaSelecionada,
          idGrupoMedidasSelecionadas:
            medidaSelecionada.idGrupoMedidasSelecionadas ||
            idGrupoMedidasSelecionadas,
          descricaoSelecao: "",
          periodicidade,
          ajusteDias,
          dataBase,
          indices,
          janelaMovel: edicaoMedida.janelaMovel,
          ...analiseValorDaMedida(medidaSelecionada, state),
        };
      }

      return indices.map(indice => ({
        ...medidaSelecionada,
        idGrupoMedidasSelecionadas:
          medidaSelecionada.idGrupoMedidasSelecionadas ||
          idGrupoMedidasSelecionadas,
        descricaoSelecao: "",
        periodicidade,
        ajusteDias,
        indice,
        dataBase,
        indices,
        janelaMovel: edicaoMedida.janelaMovel,
        ...analiseValorDaMedida(medidaSelecionada, state),
      }));
    }
  );
};

const PeriodoComIndices = () => {
  const modoSeries = useSelector(isModoSeries);

  if (modoSeries) {
    return (
      <>
        <div className="ConfiguracaoMedida">
          <SelecaoIndices selectedOptionsFirst />
        </div>

        <div className="ConfiguracaoMedida__vertical-divider" />

        <div className="ConfiguracaoMedida">
          <SelecaoJanelaMovel />
        </div>
      </>
    );
  } else {
    return (
      <div className="ConfiguracaoMedida--no-flex">
        <div className="ConfiguracaoMedida--row">
          <SelecaoIndices selectedOptionsFirst />
          <SelecaoCasasDecimais />
        </div>

        <div className="ConfiguracaoMedida__horizontal-divider" />

        <div className="ConfiguracaoMedida--row">
          <SelecaoPeriodo exibirPeriodicidade={false} />
          <div className="ConfiguracaoMedida">
            <div className="ConfiguracaoMedida__box">
              <SelecaoPeriodicidade />
            </div>

            <div className="ConfiguracaoMedida__box">
              <SelecaoAjusteDias />
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default PeriodoComIndices;
